/**
 * The SLA pill — Step 31's visible half.
 *
 * The plan's rule, and the single biggest lever on whether a queue reads as
 * "modern" or as a Christmas tree: **the SLA pill is the only saturated
 * colour on a row.** Everything else uses weight and `text-muted-foreground`.
 *
 * ── Why it renders an absolute time, not a countdown ───────────────────
 *
 * The server sends an absolute, business-hours-adjusted `dueAt`. It never
 * sends "minutes remaining", and the browser never derives one, because
 * business hours make time-remaining NON-LINEAR: a naive countdown ticks
 * through a closed Sunday and arrives at the wrong answer, confidently.
 *
 * When a clock is PAUSED we show the due time rather than a number sliding
 * downwards — a paused clock that appears to be counting is a lie, and it is
 * the most commonly botched part of any ITSM SLA UI.
 *
 * The relative hint ("in 3h") is a convenience on top of the absolute time,
 * never a replacement for it, and is suppressed when paused.
 */
import { AlertTriangle, CheckCircle2, PauseCircle, Timer } from 'lucide-react';
import { slaUrgency, type SlaClockState } from '@itsm/shared';
import { cn } from '../../lib/utils';

export interface SlaClock {
  id: string;
  kind: string;
  cycle: number;
  dueAt: number;
  pausedAt: number | null;
  achievedAt: number | null;
  breachedAt: number | null;
  targetMs: number;
}

const KIND_LABEL: Record<string, string> = {
  first_response: 'First response',
  resolution: 'Resolution',
};

/** "Mon 13:00" / "Wed 09 Sep 17:00" — absolute, and short enough for a row. */
function absolute(ms: number): string {
  const d = new Date(ms);
  const now = new Date();
  const sameWeek = Math.abs(ms - now.getTime()) < 6 * 24 * 3600_000;
  return d.toLocaleString('en-GB', {
    weekday: 'short',
    ...(sameWeek ? {} : { day: '2-digit', month: 'short' }),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** A coarse relative hint. Deliberately coarse — precision here would imply
 *  the countdown is meaningful across closed hours, which it is not. */
function relative(ms: number, now: number): string {
  const diff = ms - now;
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60_000);
  const txt =
    mins < 60
      ? `${mins}m`
      : abs < 48 * 3600_000
        ? `${Math.round(mins / 60)}h`
        : `${Math.round(mins / 1440)}d`;
  return diff >= 0 ? `in ${txt}` : `${txt} ago`;
}

export function SlaPill({
  clock,
  now = Date.now(),
  showKind = false,
  className,
}: {
  clock: SlaClock;
  now?: number;
  showKind?: boolean;
  className?: string;
}) {
  // A met promise is not urgency — it is history, and it must never wear the
  // row's one saturated colour.
  if (clock.achievedAt) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground',
          className,
        )}
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        {showKind && `${KIND_LABEL[clock.kind] ?? clock.kind}: `}met
      </span>
    );
  }

  const state: SlaClockState = {
    dueAt: clock.dueAt,
    frozenAt: clock.pausedAt,
    breached: !!clock.breachedAt,
  };
  const urgency = slaUrgency(state, now);

  // A HEALTHY CLOCK IS NOT AN EVENT. It renders as muted text with no chip and
  // no colour, because the row already carries a green "Open" status badge —
  // colouring "fine" as well meant every ordinary row shouted twice, which is
  // exactly the Christmas tree the plan's one-saturated-colour rule exists to
  // prevent. Amber and red are earned: only a clock that needs a human gets
  // the row's single accent.
  const tone =
    urgency === 'breached'
      ? 'rounded-md border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-destructive'
      : urgency === 'imminent'
        ? 'rounded-md border border-transparent bg-amber-500/15 px-1.5 py-0.5 text-amber-700 dark:text-amber-400'
        : urgency === 'paused'
          ? 'text-muted-foreground'
          : 'text-muted-foreground';

  const Icon = urgency === 'breached' ? AlertTriangle : urgency === 'paused' ? PauseCircle : Timer;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-xs',
        // Weight is reserved for the states that matter, like everything else
        // on the row.
        urgency === 'breached' || urgency === 'imminent' ? 'font-medium' : 'font-normal',
        tone,
        className,
      )}
      // Colour is never the only signal (the standing rule) — the title
      // carries the same meaning for anyone who cannot use the colour.
      title={`${KIND_LABEL[clock.kind] ?? clock.kind} ${
        urgency === 'breached' ? 'breached' : urgency === 'paused' ? 'paused, due' : 'due'
      } ${absolute(clock.dueAt)}`}
    >
      {(urgency === 'breached' || urgency === 'imminent' || urgency === 'paused') && (
        <Icon className="h-3.5 w-3.5" />
      )}
      {showKind && <span className="font-normal">{KIND_LABEL[clock.kind] ?? clock.kind}</span>}
      <span>{absolute(clock.dueAt)}</span>
      {/* Suppressed while paused: a paused clock is not counting down, and
          showing "in 3h" beside a frozen clock is the lie this avoids. */}
      {urgency !== 'paused' && <span className="font-normal opacity-70">{relative(clock.dueAt, now)}</span>}
      {urgency === 'paused' && <span className="font-normal opacity-70">paused</span>}
    </span>
  );
}

/**
 * The ticket-detail block: every clock of the CURRENT cycle, labelled.
 *
 * Older cycles are not shown here. A reopened ticket starts a new cycle by
 * design (Step 31) and the previous one is history — worth keeping, worth
 * reporting on, but not worth confusing the rail with.
 */
export function SlaPanel({ clocks, now = Date.now() }: { clocks: SlaClock[]; now?: number }) {
  if (!clocks.length) return null;
  const maxCycle = Math.max(...clocks.map((c) => c.cycle));
  const current = clocks.filter((c) => c.cycle === maxCycle);

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        SLA{maxCycle > 1 && <span className="ml-1 normal-case opacity-70">(cycle {maxCycle})</span>}
      </p>
      {current.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">{KIND_LABEL[c.kind] ?? c.kind}</span>
          <SlaPill clock={c} now={now} />
        </div>
      ))}
    </div>
  );
}
